import React, { useMemo, useState } from 'react';
import {
    Box, Typography, Select, MenuItem, Button, TextField, Collapse, IconButton, Tooltip, ListSubheader, Checkbox, Autocomplete, Chip,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import { TagPicker } from '../groups-teams-dashboard/tag-picker';
import { GT_COLORS } from '../../constants/designTokens.constants';
import { UNRESOLVED_NAME } from '../../helpers/displayName';

// ─── Styling constants (matches groups-teams dashboard) ──────
// `primary` pulls from the shared design token so a palette change in
// designTokens.constants.ts propagates here without a second edit. The
// remaining keys (border, gray scale, banner bg) are unique to this bar's
// applied-filters strip and don't yet have direct GT_COLORS equivalents —
// move them into the token file if/when they start getting reused.
const COLORS = {
    primary: GT_COLORS.primary,
    border: '#E8ECF0',
    grayMedium: '#8A94A6',
    grayDark: '#6B7A90',
    textPrimary: '#1a1a2e',
    blueLight: '#DBEAFE',
    blueDark: '#1E40AF',
    bannerBg: '#EFF6FF',
};

const selectSx = {
    minWidth: '150px',
    fontSize: '13px',
    fontFamily: 'Poppins, sans-serif',
    borderRadius: '6px',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border },
    '& .MuiSelect-select': { padding: '6px 28px 6px 10px' },
};

const textFieldSx = {
    minWidth: '170px',
    '& .MuiInputBase-root': {
        fontSize: '13px', fontFamily: 'Poppins, sans-serif',
        borderRadius: '6px', height: '36px',
    },
    '& .MuiInputBase-input': { padding: '6px 10px', fontSize: '13px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border },
};

const labelSx = {
    fontSize: '10px',
    color: COLORS.grayMedium,
    fontWeight: 500,
    textTransform: 'uppercase' as const,
    letterSpacing: '.5px',
};

const menuItemSx = { fontSize: '13px', fontFamily: 'Poppins, sans-serif' };

// ─── Types ───────────────────────────────────────────────

export interface FilterField {
    key: string;
    label: string;
    /**
     * Renderers:
     *   select              — single-value dropdown (optional `group` adds subheaders)
     *   multiselect         — legacy MUI Select with checkboxes (used by alerts/reports filters)
     *   grouped-multiselect — Autocomplete-style chip picker with search + group subheaders.
     *                          Preferred over `multiselect` for large or sectioned lists.
     *   text                — free-text input
     *   tag-picker          — chip picker keyed by tag name (TagPicker component)
     *   date-range          — preset period dropdown (Last 7/14/30/90 Days)
     *                          + From/To pickers shown only for Custom
     *                          Range; value is DateRangeValue
     *                          ({ period, from, to })
     */
    type: 'select' | 'text' | 'multiselect' | 'grouped-multiselect' | 'tag-picker' | 'date-range';
    /**
     * Value at which this field is considered "not filtering" for the
     * active-count badge — for selects whose resting state is a meaningful
     * option (e.g. the devices dateField defaults to 'createdAt') rather
     * than the empty "All".
     */
    defaultValue?: string;
    /**
     * Options for select / multiselect / grouped-multiselect. Optional `group`
     * causes the dropdown to render a non-selectable section header above each
     * contiguous block of same-`group` options. Options are sorted
     * alphabetically by `group` (then `label`) before render; pass them in
     * any order. Options with no `group` are placed under a trailing
     * "(No Group)" section.
     */
    options?: { label: string; value: string; group?: string }[];
    placeholder?: string;
    /** Tag picker options — value is stored as id[], display uses name */
    tagOptions?: { id: string; name: string; count?: number }[];
}

export interface UnifiedFilterBarProps {
    /** Primary filters — always visible in the bar */
    primaryFilters: FilterField[];
    /** Expanded filters — shown when user clicks "+ Filters" */
    expandedFilters?: FilterField[];
    /** Current filter values (keyed by FilterField.key) */
    values: Record<string, any>;
    /** Called when any filter value changes */
    onChange: (key: string, value: any) => void;
    /** Called when Apply is clicked */
    onApply: () => void;
    /** Called when Clear is clicked */
    onClear: () => void;
    /** Called when Export is clicked. May return a Promise for async CSV generation. */
    onExport?: () => void | Promise<void>;
    /** Show time period + custom date range */
    showTimePeriod?: boolean;
    timePeriod?: string;
    onTimePeriodChange?: (value: string) => void;
    customStartDate?: Dayjs | null;
    customEndDate?: Dayjs | null;
    onCustomStartDateChange?: (date: Dayjs | null) => void;
    onCustomEndDateChange?: (date: Dayjs | null) => void;
    /** Active filter pills (post-Apply) */
    appliedFilters?: { key: string; label: string; onRemove: () => void }[];
}

const TIME_PERIOD_OPTIONS = [
    { label: 'Last 7 Days', value: '7d' },
    { label: 'Last 14 Days', value: '14d' },
    { label: 'Last 30 Days', value: '30d' },
    { label: 'Last 90 Days', value: '90d' },
    { label: 'Custom Range', value: 'custom' },
];

// ─── Component ───────────────────────────────────────────

export const UnifiedFilterBar: React.FC<UnifiedFilterBarProps> = ({
    primaryFilters,
    expandedFilters = [],
    values,
    onChange,
    onApply,
    onClear,
    onExport,
    showTimePeriod,
    timePeriod,
    onTimePeriodChange,
    customStartDate,
    customEndDate,
    onCustomStartDateChange,
    onCustomEndDateChange,
    appliedFilters = [],
}) => {
    const isFilterActive = (f: FilterField, v: any) => {
        if (v === undefined || v === null || v === '') return false;
        if (Array.isArray(v)) return v.length > 0;
        // date-range value: active when a period is chosen.
        if (f.type === 'date-range') return Boolean(v.period);
        // A field sitting at its declared default isn't filtering anything —
        // don't count it toward the "More Filters" badge.
        if (f.defaultValue !== undefined && v === f.defaultValue) return false;
        return true;
    };
    const activeExpandedCount = expandedFilters.filter((f) => isFilterActive(f, values[f.key])).length;
    const [expanded, setExpanded] = useState(activeExpandedCount > 0);
    const [isExporting, setIsExporting] = useState(false);

    const handleExportClick = async () => {
        if (isExporting || !onExport) return;
        setIsExporting(true);
        toast('Download starting...', { type: 'info', autoClose: 2000 });
        try {
            await Promise.resolve(onExport());
        } catch (err) {
            console.error('[UnifiedFilterBar] export failed', err);
            toast('Export failed. Please try again.', { type: 'error' });
        } finally {
            setIsExporting(false);
        }
    };
    const isCustom = timePeriod === 'custom';
    const hasExpanded = expandedFilters.length > 0;
    const hasApplied = appliedFilters.length > 0;

    return (
        <Box sx={{ mb: 2, p: '16px 20px' }}>
            {/* Main bar */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>

                {/* Time Period */}
                {showTimePeriod && onTimePeriodChange && (
                    <>
                        <FilterSelect
                            label="Time Period"
                            value={timePeriod ?? ''}
                            options={TIME_PERIOD_OPTIONS}
                            onChange={onTimePeriodChange}
                        />
                        {isCustom && onCustomStartDateChange && onCustomEndDateChange && (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <FilterDatePicker label="Start Date" value={customStartDate ?? null} onChange={onCustomStartDateChange} maxDate={customEndDate ?? undefined} />
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', pb: '8px' }}>
                                    <Typography sx={{ fontSize: '13px', color: COLORS.grayMedium }}>to</Typography>
                                </Box>
                                <FilterDatePicker label="End Date" value={customEndDate ?? null} onChange={onCustomEndDateChange} minDate={customStartDate ?? undefined} />
                            </LocalizationProvider>
                        )}
                    </>
                )}

                {/* Primary filters */}
                {primaryFilters.map((f) => (
                    <FilterFieldRenderer key={f.key} field={f} value={values[f.key]} onChange={(v) => onChange(f.key, v)} onEnter={onApply} />
                ))}

                {/* + Filters button */}
                {hasExpanded && (
                    <Button
                        onClick={() => setExpanded(!expanded)}
                        size="small"
                        startIcon={expanded ? <FilterListIcon sx={{ fontSize: '14px !important' }} /> : <AddIcon sx={{ fontSize: '14px !important' }} />}
                        sx={{
                            textTransform: 'none', fontSize: '12px', fontWeight: 500,
                            color: expanded ? COLORS.primary : COLORS.grayDark,
                            borderRadius: '6px', whiteSpace: 'nowrap',
                            border: `1px solid ${expanded ? COLORS.primary + '40' : COLORS.border}`,
                            bgcolor: expanded ? COLORS.primary + '08' : 'transparent',
                            px: 1.5, height: '36px',
                        }}
                    >
                        {expanded ? 'Less Filters' : 'More Filters'}
                        {!expanded && activeExpandedCount > 0 && (
                            <Box
                                component="span"
                                sx={{
                                    ml: 0.75,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '18px', height: '18px',
                                    px: '5px',
                                    bgcolor: COLORS.primary,
                                    color: '#fff',
                                    fontSize: '11px', fontWeight: 700,
                                    borderRadius: '9px',
                                }}
                            >
                                {activeExpandedCount}
                            </Box>
                        )}
                    </Button>
                )}

                {/* Apply / Clear */}
                <Button
                    onClick={onApply}
                    sx={{
                        padding: '6px 16px', borderRadius: '6px',
                        fontSize: '13px', fontWeight: 500,
                        backgroundColor: COLORS.primary, color: '#fff',
                        textTransform: 'none', fontFamily: 'Poppins, sans-serif',
                        height: '36px',
                        '&:hover': { backgroundColor: '#2b5a8a' },
                    }}
                >
                    Apply
                </Button>
                <Typography
                    onClick={onClear}
                    sx={{
                        padding: '6px 10px', fontSize: '13px',
                        cursor: 'pointer', color: COLORS.grayDark,
                        '&:hover': { color: COLORS.textPrimary },
                    }}
                >
                    Clear
                </Typography>

                {/* Spacer + Export */}
                <Box sx={{ flex: 1 }} />
                {onExport && (
                    <Typography
                        onClick={isExporting ? undefined : handleExportClick}
                        aria-disabled={isExporting}
                        sx={{
                            fontSize: '12px',
                            color: isExporting ? COLORS.grayMedium : COLORS.primary,
                            cursor: isExporting ? 'not-allowed' : 'pointer',
                            fontWeight: 500,
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            opacity: isExporting ? 0.6 : 1,
                            pointerEvents: isExporting ? 'none' : 'auto',
                            '&:hover': { textDecoration: isExporting ? 'none' : 'underline' },
                        }}
                    >
                        <FileDownloadOutlinedIcon sx={{ fontSize: '14px' }} />
                        {isExporting ? 'Exporting...' : 'Export'}
                    </Typography>
                )}
            </Box>

            {/* Expanded filters. Top-aligned (not flex-end): date-range
                fields are taller than plain selects (quick-pick chips hang
                below the pickers), and bottom-aligning shoved the shorter
                fields' inputs off the shared horizontal plane. With equal-
                height fields the two alignments render identically. */}
            <Collapse in={expanded}>
                <Box sx={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap',
                    mt: 1.5, pt: 1.5, borderTop: `1px solid ${COLORS.border}`,
                }}>
                    {expandedFilters.map((f) => (
                        <FilterFieldRenderer key={f.key} field={f} value={values[f.key]} onChange={(v) => onChange(f.key, v)} onEnter={onApply} />
                    ))}
                </Box>
            </Collapse>

            {/* Applied filter pills */}
            {hasApplied && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    mt: 1, p: '6px 12px',
                    bgcolor: COLORS.bannerBg, borderRadius: '6px',
                    border: `1px solid ${COLORS.blueLight}`,
                }}>
                    <Typography sx={{ fontSize: '11px', color: COLORS.grayDark, fontWeight: 500, whiteSpace: 'nowrap' }}>
                        Filtered by:
                    </Typography>
                    {appliedFilters.map((f) => (
                        <Box
                            key={f.key}
                            sx={{
                                display: 'inline-flex', alignItems: 'center', gap: '3px',
                                padding: '2px 6px 2px 8px', borderRadius: '10px',
                                fontSize: '11px', fontWeight: 500,
                                bgcolor: COLORS.blueLight, color: COLORS.blueDark,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {f.label}
                            <Box
                                onClick={f.onRemove}
                                sx={{
                                    width: 14, height: 14, borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer', '&:hover': { bgcolor: '#BFDBFE' },
                                }}
                            >
                                <CloseIcon sx={{ fontSize: '10px' }} />
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

// ─── Sub-components ──────────────────────────────────────

function FilterFieldRenderer({ field, value, onChange, onEnter }: { field: FilterField; value: any; onChange: (v: any) => void; onEnter?: () => void }) {
    switch (field.type) {
        case 'select':
            return <FilterSelect label={field.label} value={value || ''} options={field.options || []} onChange={onChange} />;
        case 'text':
            return <FilterText label={field.label} value={value || ''} onChange={onChange} placeholder={field.placeholder} onEnter={onEnter} />;
        case 'multiselect':
            return <FilterMultiSelect label={field.label} value={value || []} options={field.options || []} onChange={onChange} />;
        case 'grouped-multiselect':
            return (
                <FilterGroupedMultiSelect
                    label={field.label}
                    value={value || []}
                    options={field.options || []}
                    onChange={onChange}
                    placeholder={field.placeholder}
                />
            );
        case 'date-range':
            return (
                <FilterDateRange
                    label={field.label}
                    value={value || { period: '', from: null, to: null }}
                    onChange={onChange}
                />
            );
        case 'tag-picker':
            return (
                <FilterTagPicker
                    label={field.label}
                    tagOptions={field.tagOptions || []}
                    value={value || []}
                    onChange={onChange}
                    placeholder={field.placeholder}
                />
            );
        default:
            return null;
    }
}

/** Tag picker field — stores an array of tag IDs; TagPicker works on names, so we map both directions. */
function FilterTagPicker({ label, tagOptions, value, onChange, placeholder }: {
    label: string;
    tagOptions: { id: string; name: string; count?: number }[];
    value: string[]; // tag ids
    onChange: (v: string[]) => void;
    placeholder?: string;
}) {
    const { idToName, nameToId, pickerOptions } = useMemo(() => {
        const idToName = new Map<string, string>();
        const nameToId = new Map<string, string>();
        for (const t of tagOptions) {
            idToName.set(t.id, t.name);
            nameToId.set(t.name, t.id);
        }
        const pickerOptions = tagOptions.map((t) => ({ name: t.name, count: t.count ?? 0 }));
        return { idToName, nameToId, pickerOptions };
    }, [tagOptions]);

    const selectedNames = value
        .map((id) => idToName.get(id))
        .filter((n): n is string => !!n);

    const handleChange = (names: string[]) => {
        const ids = names
            .map((n) => nameToId.get(n))
            .filter((id): id is string => !!id);
        onChange(ids);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography sx={labelSx}>{label}</Typography>
            <TagPicker
                options={pickerOptions}
                selected={selectedNames}
                onChange={handleChange}
                placeholder={placeholder || 'Select tags…'}
                colorVariant="blue"
                showCount={false}
            />
        </Box>
    );
}

// Sort options first by group (alphabetical, "No Group" pushed to end),
// then by label within each group. Returns a flat list of either an option
// or a { subheader: string } sentinel — render order is preserved.
type GroupedOptionRow =
    | { label: string; value: string; group?: string }
    | { subheader: string };
const buildGroupedRows = (options: { label: string; value: string; group?: string }[]): GroupedOptionRow[] => {
    const anyGrouped = options.some((o) => o.group);
    if (!anyGrouped) {
        return [...options].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    }
    const NO_GROUP_LABEL = '(No Group)';
    const byGroup = new Map<string, { label: string; value: string; group?: string }[]>();
    for (const o of options) {
        const key = o.group || NO_GROUP_LABEL;
        const bucket = byGroup.get(key) || [];
        bucket.push(o);
        byGroup.set(key, bucket);
    }
    const groupKeys = Array.from(byGroup.keys()).sort((a, b) => {
        if (a === NO_GROUP_LABEL) return 1;
        if (b === NO_GROUP_LABEL) return -1;
        return a.localeCompare(b);
    });
    const rows: GroupedOptionRow[] = [];
    for (const g of groupKeys) {
        rows.push({ subheader: g });
        const bucket = byGroup.get(g) || [];
        bucket.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
        for (const o of bucket) rows.push(o);
    }
    return rows;
};

const subheaderSx = {
    fontSize: '11px',
    fontFamily: 'Poppins, sans-serif',
    color: COLORS.grayDark,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '.5px',
    lineHeight: '28px',
    bgcolor: '#F4F6F8',
};

function FilterSelect({ label, value, options, onChange }: {
    label: string; value: string; options: { label: string; value: string; group?: string }[]; onChange: (v: string) => void;
}) {
    const rows = useMemo(() => buildGroupedRows(options), [options]);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography sx={labelSx}>{label}</Typography>
            <Select value={value} onChange={(e) => onChange(e.target.value)} size="small" sx={selectSx} displayEmpty>
                <MenuItem value="" sx={menuItemSx}><em>All</em></MenuItem>
                {rows.map((r, i) => 'subheader' in r
                    ? <ListSubheader key={`sh-${i}`} sx={subheaderSx} disableSticky>{r.subheader}</ListSubheader>
                    : <MenuItem key={r.value} value={r.value} sx={menuItemSx}>{r.label}</MenuItem>
                )}
            </Select>
        </Box>
    );
}

function FilterText({ label, value, onChange, placeholder, onEnter }: {
    label: string; value: string; onChange: (v: string) => void; placeholder?: string; onEnter?: () => void;
}) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography sx={labelSx}>{label}</Typography>
            <TextField
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) onEnter(); }}
                placeholder={placeholder || `Filter by ${label.toLowerCase()}...`}
                size="small"
                sx={textFieldSx}
            />
        </Box>
    );
}

function FilterMultiSelect({ label, value, options, onChange }: {
    label: string; value: string[]; options: { label: string; value: string; group?: string }[]; onChange: (v: string[]) => void;
}) {
    const labelMap = useMemo(
        () => Object.fromEntries(options.map((o) => [o.value, o.label])),
        [options],
    );
    const rows = useMemo(() => buildGroupedRows(options), [options]);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography sx={labelSx}>{label}</Typography>
            <Select
                multiple
                value={value}
                onChange={(e) => onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                size="small"
                sx={{ ...selectSx, minWidth: '180px' }}
                displayEmpty
                renderValue={(selected) => {
                    if ((selected as string[]).length === 0) return <em style={{ color: COLORS.grayMedium, fontSize: '13px' }}>All</em>;
                    // Never fall back to the raw value — for id-valued fields
                    // (e.g. the Alerts Tags filter) that would print an ObjectId.
                    return (selected as string[]).map((v) => labelMap[v] || UNRESOLVED_NAME).join(', ');
                }}
            >
                {rows.map((r, i) => 'subheader' in r
                    ? <ListSubheader key={`sh-${i}`} sx={subheaderSx} disableSticky>{r.subheader}</ListSubheader>
                    : (
                        <MenuItem key={r.value} value={r.value} sx={menuItemSx}>
                            <Checkbox checked={value.indexOf(r.value) > -1} size="small" sx={{ p: 0.5, mr: 0.5 }} />
                            {r.label}
                        </MenuItem>
                    )
                )}
            </Select>
        </Box>
    );
}

// Autocomplete-backed chip picker with search-as-you-type and group
// subheaders. Preferred over `FilterMultiSelect` for large or grouped
// lists — the chip rendering keeps the trigger compact even with many
// selections, and the type-to-filter input scales past ~20 options.
function FilterGroupedMultiSelect({ label, value, options, onChange, placeholder }: {
    label: string;
    value: string[];
    options: { label: string; value: string; group?: string }[];
    onChange: (v: string[]) => void;
    placeholder?: string;
}) {
    // Stable Option objects for `value` resolution — Autocomplete is identity-
    // sensitive (uses `isOptionEqualToValue` for matching). Building this from
    // `options` keeps `value` in sync when the parent re-renders the array.
    const byValue = useMemo(() => {
        const m = new Map<string, { label: string; value: string; group?: string }>();
        for (const o of options) m.set(o.value, o);
        return m;
    }, [options]);

    const selectedOptions = useMemo(
        () => value.map((v) => byValue.get(v)).filter((o): o is { label: string; value: string; group?: string } => !!o),
        [value, byValue],
    );

    // Sort options once so `groupBy` produces alphabetically-ordered sections.
    // Autocomplete preserves option order for group display.
    const sortedOptions = useMemo(() => {
        const NO_GROUP = '(No Group)';
        return [...options].sort((a, b) => {
            const ga = a.group || NO_GROUP;
            const gb = b.group || NO_GROUP;
            if (ga !== gb) {
                if (ga === NO_GROUP) return 1;
                if (gb === NO_GROUP) return -1;
                return ga.localeCompare(gb);
            }
            return (a.label || '').localeCompare(b.label || '');
        });
    }, [options]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography sx={labelSx}>{label}</Typography>
            <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={sortedOptions}
                value={selectedOptions}
                onChange={(_, next) => onChange(next.map((o) => o.value))}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                groupBy={(o) => o.group || '(No Group)'}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => {
                        const { key, ...chipProps } = getTagProps({ index });
                        return (
                            <Chip
                                key={key}
                                {...chipProps}
                                size="small"
                                label={option.label}
                                sx={{
                                    height: '22px',
                                    fontSize: '12px',
                                    fontFamily: 'Poppins, sans-serif',
                                    bgcolor: COLORS.blueLight,
                                    color: COLORS.blueDark,
                                    '& .MuiChip-deleteIcon': { fontSize: '14px', color: COLORS.blueDark },
                                }}
                            />
                        );
                    })
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        // The visible <Typography> label sits in a separate
                        // node above the input, so screen readers don't
                        // associate it with the field. inputProps.aria-label
                        // restores that association.
                        inputProps={{ ...params.inputProps, 'aria-label': label }}
                        placeholder={value.length === 0 ? (placeholder || 'All') : ''}
                        sx={{
                            minWidth: '220px',
                            '& .MuiInputBase-root': {
                                fontSize: '13px',
                                fontFamily: 'Poppins, sans-serif',
                                borderRadius: '6px',
                                minHeight: '36px',
                                padding: '2px 8px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border },
                        }}
                    />
                )}
                renderOption={(props, option, { selected }) => {
                    const { key, ...optProps } = props as any;
                    return (
                        <li key={key} {...optProps} style={{ fontSize: '13px', fontFamily: 'Poppins, sans-serif', padding: '4px 10px' }}>
                            <Checkbox checked={selected} size="small" sx={{ p: 0.5, mr: 0.5 }} />
                            {option.label}
                        </li>
                    );
                }}
                renderGroup={(params) => (
                    <li key={params.key}>
                        <Box sx={{
                            ...subheaderSx,
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            px: '10px',
                        }}>
                            {params.group}
                        </Box>
                        <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                    </li>
                )}
            />
        </Box>
    );
}

// Per-field version of the page-level Time Period control: a preset
// dropdown (Last 7/14/30/90 Days) whose From/To pickers only appear when
// "Custom Range" is selected — same UX as the existing date filters.
// Presets are stored as the period token and resolved to concrete dates by
// the consumer at Apply time.
export type DateRangeValue = { period: string; from: Dayjs | null; to: Dayjs | null };

export const DATE_RANGE_PERIOD_OPTIONS = TIME_PERIOD_OPTIONS;

function FilterDateRange({ label, value, onChange }: {
    label: string;
    value: DateRangeValue;
    onChange: (v: DateRangeValue) => void;
}) {
    // Box recipe copied from the grouped-multiselect neighbors (minHeight
    // 36px + 2px vertical padding + border) so the controls in the expanded
    // row render at identical height by construction, not by pixel-matching.
    const periodSelectSx = {
        ...selectSx,
        minHeight: '36px',
        padding: '2px 0',
        '& .MuiSelect-select': {
            padding: '6px 28px 6px 10px',
            display: 'flex',
            alignItems: 'center',
        },
    };
    const inputSx = {
        width: '138px',
        '& .MuiInputBase-root': {
            fontSize: '13px', fontFamily: 'Poppins, sans-serif', borderRadius: '6px',
            minHeight: '36px', padding: '2px 0',
        },
        '& .MuiInputBase-input': { padding: '6px 0 6px 10px', fontSize: '13px' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border },
    };
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography sx={labelSx}>{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Select
                    value={value.period}
                    onChange={(e) => {
                        const period = e.target.value;
                        // Leaving custom drops the picked dates so they can't
                        // silently resurface later.
                        onChange(period === 'custom' ? { ...value, period } : { period, from: null, to: null });
                    }}
                    size="small"
                    sx={periodSelectSx}
                    displayEmpty
                >
                    <MenuItem value="" sx={menuItemSx}><em>All</em></MenuItem>
                    {TIME_PERIOD_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value} sx={menuItemSx}>{o.label}</MenuItem>
                    ))}
                </Select>
                {value.period === 'custom' && (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={value.from}
                            onChange={(d) => onChange({ ...value, from: d })}
                            maxDate={value.to ?? undefined}
                            renderInput={(params) => (
                                <TextField {...params} size="small" placeholder="Start" sx={inputSx} />
                            )}
                        />
                        <Typography sx={{ color: COLORS.grayMedium, fontSize: '13px' }}>–</Typography>
                        <DatePicker
                            value={value.to}
                            onChange={(d) => onChange({ ...value, to: d })}
                            minDate={value.from ?? undefined}
                            renderInput={(params) => (
                                <TextField {...params} size="small" placeholder="End" sx={inputSx} />
                            )}
                        />
                    </LocalizationProvider>
                )}
            </Box>
        </Box>
    );
}

function FilterDatePicker({ label, value, onChange, minDate, maxDate }: {
    label: string; value: Dayjs | null; onChange: (d: Dayjs | null) => void; minDate?: Dayjs; maxDate?: Dayjs;
}) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography sx={labelSx}>{label}</Typography>
            <DatePicker
                value={value}
                onChange={onChange}
                minDate={minDate}
                maxDate={maxDate}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        placeholder="MM/DD/YYYY"
                        sx={{
                            width: '150px',
                            '& .MuiInputBase-root': { fontSize: '13px', fontFamily: 'Poppins, sans-serif', borderRadius: '6px', height: '36px' },
                            '& .MuiInputBase-input': { padding: '6px 0 6px 10px', fontSize: '13px' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border },
                        }}
                    />
                )}
            />
        </Box>
    );
}
